import * as React from "react";

import { GridSchema, IRow, TableColum } from "../interfaces";
import { IDispatcher } from "../../../helpers/Dispatcher";
import { IControlError } from "../../../../core/Controls";

import { cleanObj, getEntries, getKeys, getValues, rows2cols, uid } from "../../../helpers/Utils";
import {
	isExistEdit,
	createHeaderCols,
	createRows,
	getColumnFields,
	getValuesForSubmit,
	getLengthDependence,
	getValidateErrors,
} from "./helpers";

import { TableHead } from "./TableHead";
import { TableBody } from "./TableBody";
import Field from "../../components/Field";
import ErrorAlert from "../../components/ErrorAlert";

type WithoutLastElement = Record<string, TableColum & { isLastElement?: boolean }>;

export interface GridEditableState {
	rows: IRow;
	columns: Record<string, TableColum>;
	errors: Record<string, Record<string, string>>;
	fieldsRef: IRow;
	updatingRow: any;
	dependenceLength: number;
}

export interface GridEditableProps {
	api: unknown;
	values: Array<{ controlid: string; value: unknown }>;
	schema: GridSchema;
	dispatcher: IDispatcher;
	error: IControlError | undefined;
}

// TODO: Need rebuild on Functional Component
class GridEditable extends React.Component<GridEditableProps, GridEditableState> {
	state = {
		rows: {},
		columns: {},
		errors: {},
		fieldsRef: {},
		updatingRow: null,
		dependenceLength: 0,
	};

	componentDidMount() {
		const { schema, values } = this.props;
		const _updatedValue = schema?.controlValue || schema?.value;
		const _dependenceLength = getLengthDependence(values, schema?.extra?.lengthFromId);

		this.setState({
			rows: createRows(_updatedValue, schema?.template?.columns, _dependenceLength),
			columns: createHeaderCols(schema.columns, schema?.template),
			dependenceLength: _dependenceLength,
		});
	}

	componentDidUpdate(prevProps: Readonly<GridEditableProps>) {
		const { schema } = this.props;
		const { dependenceLength } = this.state;
		const _updatedValue = schema?.controlValue || schema?.value;
		const _prevUpdatedValue = prevProps.schema?.controlValue || prevProps.schema?.value;

		if (_prevUpdatedValue !== _updatedValue) {
			const { fieldsRef } = this.state;

			this.setState({
				rows: { ...createRows(_updatedValue, schema?.template?.columns, dependenceLength), ...fieldsRef },
			});
		}
	}

	getFieldsWithSumRow = (newValue, keyRow) => {
		// Итого
		// @ts-ignore
		if (getValues(this.state.columns).find((column) => column?.defaultValue?.includes("sumRow"))) {
			const columnWhereNeedSum = getValues(this.state.columns).find((column) =>
				// @ts-ignore
				column?.defaultValue?.includes("sumRow")
			);
			let sum = 0;

			for (const column of getValues(this.state.columns)) {
				// @ts-ignore
				if (column?.type?.toUpperCase() === "FLOAT" && column.field !== columnWhereNeedSum.field) {
					// @ts-ignore
					const value = newValue.fieldsRef[keyRow]?.[column.field] ?? 0;
					sum += +value;
				}
			}

			if (this.props.schema?.template?.columns) {
				for (const templateItem of this.props.schema.template.columns) {
					// @ts-ignore
					if (templateItem?.extra?.sumRow && templateItem.field !== columnWhereNeedSum.field && keyRow) {
						if (newValue?.fieldsRef[keyRow].groups.length) {
							for (const group of newValue?.fieldsRef[keyRow].groups) {
								const value = group?.[templateItem.field] ?? 0;
								sum += +value;
							}
						}
					}
				}
			}

			// @ts-ignore
			newValue.fieldsRef[keyRow][columnWhereNeedSum.field] = sum;
		}

		return newValue;
	};

	setFieldsRef = (keyRow: string | number, indexGroup?: number) => (data) => {
		this.setState(({ fieldsRef }) => {
			let nextState;
			if (typeof indexGroup === "number") {
				const shallowGroup = [...fieldsRef[keyRow].groups];
				shallowGroup[indexGroup] = data;

				nextState = this.getFieldsWithSumRow(
					{
						fieldsRef: {
							...fieldsRef,
							[keyRow]: {
								...fieldsRef[keyRow],
								groups: shallowGroup,
							},
						},
					},
					keyRow
				);

				return nextState;
			}

			nextState = this.getFieldsWithSumRow(
				{
					fieldsRef: {
						...fieldsRef,
						[keyRow]: data,
					},
				},
				keyRow
			);

			return nextState;
		});
	};

	createRow = (event?: React.MouseEvent<HTMLButtonElement>) => {
		event?.preventDefault();

		const { schema } = this.props;
		const { dependenceLength } = this.state;
		const _uid = `key_${uid()}`;
		const fields = getColumnFields(schema.columns);
		let groupFields = null;
		let _groups = null;

		if (schema?.template) {
			groupFields = getColumnFields(schema?.template?.columns);

			_groups = Array.from({ length: dependenceLength ?? 0 }, () => ({
				key: `stage_${uid()}`,
				...groupFields,
			}));
		}

		const createdRow = {
			...fields,
			key: _uid,
			isEdit: true,
			groups: _groups,
		};

		this.setState(({ rows, fieldsRef }) => ({
			rows: {
				...rows,
				[_uid]: createdRow,
			},
			fieldsRef: {
				...fieldsRef,
				[_uid]: createdRow,
			},
		}));
	};

	editRow = (key: string | number) => (event?: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();

		this.setState(({ rows, fieldsRef }) => {
			const shallowRows = { ...rows };

			shallowRows[key].isEdit = true;

			return {
				rows: shallowRows,
				fieldsRef: { ...fieldsRef, ...shallowRows },
				updatingRow: shallowRows[key],
			};
		});
	};

	deleteRow = (key: string | number) => (event?: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();

		const { schema, dispatcher } = this.props;
		const _updatedValue = schema?.controlValue || schema?.value;
		const { rows, fieldsRef, dependenceLength } = this.state;

		const shallowRows = { ...rows };
		const shallowRowsRef = { ...fieldsRef };
		const isExistRowInValue = _updatedValue?.find((value) => value.key === key);

		delete shallowRows?.[key];
		delete shallowRowsRef?.[key];
		
		if (isExistRowInValue) {
			this.setState({
				rows: {
					...createRows(
						getValuesForSubmit(shallowRows, dependenceLength),
						schema?.template?.columns,
						dependenceLength
					)
				},
				fieldsRef: shallowRowsRef,
			});
			dispatcher.onBlur(schema.id, getValuesForSubmit(shallowRows, dependenceLength), true, undefined);
			this.actionAfterSomeActionWithTable(getValuesForSubmit(shallowRows, dependenceLength), "delete", isExistRowInValue, key);
			
			return
		} else {
			this.setState({
				rows: shallowRows,
				fieldsRef: shallowRowsRef,
			});
		}
	};

	getLastRowForSum = (values): { [key: string]: any } => {
		if (!values || values?.find((item) => item?.key === "sumCol")) {
			return null;
		}

		const { schema } = this.props;

		let result = {};

		if (schema?.extra?.funcRow) {
			// eslint-disable-next-line guard-for-in
			for (const index in schema.extra.funcRow) {
				const groups = [];

				const item: { target: string; action: string } = schema.extra.funcRow[index];
				if (item.action === "sumCol") {
					result[item.target] = values.reduce((acc, itemState) => {
						let itemStateTarget;
						if (itemState?.[item.target]) {
							itemStateTarget = +itemState[item.target];
						} else if (itemState.groups) {
							groups.push(itemState.groups);
						}

						return acc + itemStateTarget;
					}, 0);
				}

				if (groups.length) {
					result = cleanObj({
						...result,
						...rows2cols(groups.reverse()).reduce((groupsAcc, groupsItem, _index) => {
							const key = `group_${_index}`;
							return {
								...groupsAcc,
								[key]: groupsItem.reduce((a, b) => a + +b[item.target], 0),
							};
						}, {}),
					});
				}
			}
		}

		// Заполним недостающие поля в последней строке (если она не нулл)
		if (getKeys(result).length > 0) {
			// Определим обьект с полями которых нет
			const needFields = [];
			for (const value in values[values.length - 1]) {
				if (!result?.[value]) {
					needFields.push(value);
				}
			}

			// Запишем недостающие поля
			for (const needField of needFields) {
				if (needField === "key" || needField === "isEdit" || needField === "groups") {
					continue;
				}
				result[needField] = "";
			}
		}

		return getKeys(result).length > 0 ? result : null;
	};

	rejectEditRows = (event?: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		const { schema } = this.props;
		const { dependenceLength } = this.state;

		this.setState({
			fieldsRef: {},
			errors: {},
			rows: createRows(
				getValuesForSubmit(schema?.controlValue || schema?.value, dependenceLength),
				schema?.template?.columns,
				dependenceLength
			),
		});
	};

	actionAfterSomeActionWithTable = (
		nextValue?: any,
		action?: "add" | "delete" | "update",
		differencePrevValues?: any,
		key?: string | number
	) => {
		const { schema, dispatcher } = this.props;
		const { rows, fieldsRef, dependenceLength } = this.state;
		const shallowCopy = { ...rows, ...fieldsRef };
		const values = Array.isArray(getValuesForSubmit(shallowCopy, dependenceLength))
			? getValuesForSubmit(shallowCopy, dependenceLength)
			: [];

		let itSimpleBlur = true;

		// Добавляем только те значения которые нужны в поле

		for (const column of schema.columns) {
			if (column?.extra?.duplicateOrSumValueForId) {
				itSimpleBlur = false;
				const sum = +(nextValue?.[column.field] ?? 0);
				const valueFromId = +(dispatcher.pullValue(column.extra.duplicateOrSumValueForId) ?? 0);
				let resultAction;

				if (action === "add") {
					resultAction = valueFromId + sum;
				} else if (action === "delete") {
					resultAction = valueFromId - +(differencePrevValues?.[column.field] ?? 0);
				} else if (action === "update") {
					resultAction = valueFromId - +(differencePrevValues?.[column.field] ?? 0) + sum;
				}

				if (action !== "delete") {
					dispatcher.onBlur(schema.id, getValuesForSubmit(shallowCopy, dependenceLength), true, undefined);
				}
				dispatcher.onBlur(column.extra.duplicateOrSumValueForId, resultAction, true, undefined);
			}
		}

		if (itSimpleBlur && action !== "delete") {
			// TODO: validate fieldRef & submit
			dispatcher.onBlur(schema.id, getValuesForSubmit(shallowCopy, dependenceLength), true, undefined);
		}

		if (schema?.extra?.duplicateValueInGridId && action === "delete") {

			const pulledValues = dispatcher.pullValue(schema.extra.duplicateValueInGridId);

			const filteredValues = pulledValues?.filter((val) => val.key !== key);

			setTimeout(() => {
				dispatcher.onBlur(schema.extra.duplicateValueInGridId, filteredValues, true, undefined);
			}, 0)

			return
		}
		
		// Добавляем все значения в таблицу или редактируем
		if (schema?.extra?.duplicateValueInGridId && (action === "add" || action === "update") ) {
			const pulledValues = dispatcher.pullValue(schema.extra.duplicateValueInGridId);

			const pulledSchema = dispatcher
				.pullControlSchema(schema.extra.duplicateValueInGridId)
				.columns.reduce((acc, item) => [...acc, item.field], []);
				
			const mergedValues = values.map((item, index) => {
				const havePulledValue = pulledValues?.[index];

				const valueWhoNeed = (action === "add") ? 
					pulledSchema.reduce(
						(acc, itemKey) => ({
							...acc,
							[itemKey]: item[itemKey] ?? "",
						}),
						{}
					) : 
					pulledSchema.reduce(
						(acc, itemKey) => ({
							...acc,
							[itemKey]: item[itemKey] ? item[itemKey] : havePulledValue[itemKey]
						}),
						{}
					)

				return (action === "add") ? 
					(havePulledValue ? pulledValues[index] : valueWhoNeed) : 
					valueWhoNeed
			});
			dispatcher.onBlur(schema.extra.duplicateValueInGridId, mergedValues, true, undefined);
		}
	};

	saveRows = (event?: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		const { schema } = this.props;
		const { columns, rows, fieldsRef, updatingRow, dependenceLength } = this.state;
		const shallowCopy = { ...rows, ...fieldsRef };
		const fieldsForErrors = getEntries(fieldsRef).reduce(
			(acc, [key, value]) => {
				// @ts-ignore
				if (value?.isEdit) {
					acc[key] = value
				}
				return acc
			}, 
			{} as IRow
		);

		const _errors = getValidateErrors(fieldsForErrors, columns);

		if (_errors) {
			this.setState({ errors: _errors });

			return;
		}

		if (updatingRow) {
			this.actionAfterSomeActionWithTable(getValuesForSubmit(fieldsRef, dependenceLength)[0], "update", updatingRow);
		} else {
			this.actionAfterSomeActionWithTable(getValuesForSubmit(fieldsRef, dependenceLength)[0], "add");
		}

		this.setState({
			rows: {
				...createRows(
					getValuesForSubmit(shallowCopy, dependenceLength), 
					schema?.template?.columns, 
					dependenceLength
				),
			},
			errors: {},
			fieldsRef: {},
			updatingRow: null,
		});
	};

	render() {
		const {
			schema: { disabled, extra, maxRowCount, template },
			api,
			dispatcher,
			error,
		} = this.props;
		const { rows, columns, errors, fieldsRef, dependenceLength } = this.state;
		const lastElement = getValues<WithoutLastElement>(columns)?.find((item) => item?.isLastElement);
		const isEditable = isExistEdit(rows);
		const lengthRows = getKeys<IRow>(rows)?.length;
		const isVisibleAddButton =
			(maxRowCount ? lengthRows < maxRowCount : true) &&
			(extra?.add_row_enabled !== undefined ? extra.add_row_enabled : true) &&
			(!this.props.schema.columns.find(elem => elem.valueFromField));

		return (
			<>
				<div className="table-row">
					<table className="table table-bordered">
						<TableHead
							columns={columns}
							template={template}
							lastElement={lastElement}
							dependenceLength={dependenceLength}
						/>
						<TableBody
							schema={this.props.schema}
							api={api}
							dispatcher={dispatcher}
							disabled={disabled}
							columns={columns}
							rows={rows}
							errors={errors}
							fieldsRef={fieldsRef}
							setFieldsRef={this.setFieldsRef}
							editRow={this.editRow}
							deleteRow={this.deleteRow}
							getLastRowForSum={this.getLastRowForSum}
							lastElementName={lastElement?.field}
							dependenceLength={dependenceLength}
						/>
					</table>
					{!lengthRows && <p className="empty-table">Нет данных для отображения</p>}
				</div>

				{!disabled && (
					<div className="buttons">
						{isVisibleAddButton && (
							<button type="button" className="btn btn-info react-bs-table-add-btn" onClick={this.createRow}>
								<span className="glyphicon glyphicon-plus" />
								Добавить
							</button>
						)}
						{isEditable && (
							<>
								<button type="button" className="btn btn-success" onClick={this.saveRows}>
									Сохранить
								</button>
								<button type="button" className="btn btn-danger" onClick={this.rejectEditRows}>
									Отменить
								</button>
							</>
						)}
					</div>
				)}

				<ErrorAlert errorMessage={error?.message || null} />
			</>
		);
	}
}

export const createGridEditable = (props: GridEditableProps) =>
	props.schema.visible === undefined || props.schema.visible ? (
		<Field {...props.schema}>
			<GridEditable {...props} />
		</Field>
	) : (
		<div />
	);
